/**
 * AdminDeletionPanel.tsx
 * 
 * PRIV-200.8: Admin-Initiated Deletion UX for VeraScore Admins
 * 
 * Allows admins to initiate member deletion from Privacy KPI Dashboard
 * for compliance, fraud, or operational reasons.
 * 
 * Access: verascore_admin role only
 */

import { FC, useState } from 'react';
import { FiSearch, FiTrash2, FiAlertTriangle, FiCheckCircle, FiClock, FiX } from 'react-icons/fi';
import PostApi from '../../api/PostApi';

// Types
interface MemberSearchResult {
  uuid: string;
  member_id: string;
  email: string;
  first_name: string;
  last_name: string;
  lifecycle_status: string;
  created_at: string;
}

interface DeletionJob {
  job_id: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  created_at: string;
  completed_at?: string;
  error_message?: string;
}

type DeletionReason = 'fraud' | 'compliance' | 'test_cleanup' | 'legal_request' | 'other';

const DELETION_REASONS: { value: DeletionReason; label: string }[] = [
  { value: 'fraud', label: 'Fraud / Suspicious Activity' },
  { value: 'compliance', label: 'Compliance Request' },
  { value: 'test_cleanup', label: 'Test Data Cleanup' },
  { value: 'legal_request', label: 'Legal / Regulatory Request' },
  { value: 'other', label: 'Other (specify in ticket)' },
];

export const AdminDeletionPanel: FC = () => {
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<MemberSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Deletion form state
  const [selectedMember, setSelectedMember] = useState<MemberSearchResult | null>(null);
  const [deletionReason, setDeletionReason] = useState<DeletionReason>('compliance');
  const [ticketId, setTicketId] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Job tracking state
  const [currentJob, setCurrentJob] = useState<DeletionJob | null>(null);

  /**
   * Search for member by email or member_id
   */
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchError('Please enter an email or member ID');
      return;
    }

    setSearching(true);
    setSearchError(null);
    setSearchResults([]);

    try {
      // TODO: Replace with actual API endpoint when available
      // For now, using Supabase direct query (will need backend endpoint)
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/verascore/admin/members/search?q=${encodeURIComponent(searchQuery)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'VS-Client-Id': import.meta.env.VITE_VS_CLIENT_ID,
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`Search failed: ${response.statusText}`);
      }

      const data = await response.json();
      setSearchResults(data.data || []);

      if (data.data.length === 0) {
        setSearchError('No members found matching your search');
      }
    } catch (error) {
      console.error('[AdminDeletionPanel] Search error:', error);
      setSearchError('Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  /**
   * Select member for deletion
   */
  const handleSelectMember = (member: MemberSearchResult) => {
    setSelectedMember(member);
    setSearchResults([]);
    setSearchQuery('');
  };

  /**
   * Open confirmation dialog
   */
  const handleInitiateDeletion = () => {
    if (!selectedMember) return;
    if (!ticketId.trim()) {
      alert('Ticket ID is required for audit trail');
      return;
    }
    setShowConfirmDialog(true);
  };

  /**
   * Confirm and execute deletion
   */
  const handleConfirmDeletion = () => {
    if (!selectedMember) return;

    setSubmitting(true);
    setShowConfirmDialog(false);

    const payload = {
      member_id: selectedMember.member_id,
      reason: deletionReason,
      ticket_id: ticketId,
      admin_initiated: true,
    };

    PostApi<{ job_id: string; status: string; created_at: string }>(
      `${import.meta.env.VITE_API_URL}/verascore/admin/members/${selectedMember.member_id}/delete`,
      payload,
      (data) => {
        console.log('[AdminDeletionPanel] Deletion initiated:', data);
        setCurrentJob({
          job_id: data.job_id,
          status: data.status as any,
          created_at: data.created_at,
        });
        setSubmitting(false);
        // Reset form
        setSelectedMember(null);
        setTicketId('');
        setDeletionReason('compliance');
      },
      (error) => {
        console.error('[AdminDeletionPanel] Deletion failed:', error);
        alert('Deletion failed. Please check the console for details.');
        setSubmitting(false);
      },
      (error) => {
        console.error('[AdminDeletionPanel] Deletion rejected:', error);
        alert(`Deletion request rejected: ${error}`);
        setSubmitting(false);
      },
      'compliance_audit' // Purpose for audit trail
    );
  };

  /**
   * Cancel deletion dialog
   */
  const handleCancelDialog = () => {
    setShowConfirmDialog(false);
  };

  /**
   * Clear selected member
   */
  const handleClearSelection = () => {
    setSelectedMember(null);
    setTicketId('');
    setDeletionReason('compliance');
  };

  /**
   * Get status badge color
   */
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  /**
   * Get status icon
   */
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <FiCheckCircle className="text-green-600" />;
      case 'failed':
        return <FiAlertTriangle className="text-red-600" />;
      case 'in_progress':
        return <FiClock className="text-blue-600 animate-spin" />;
      case 'pending':
        return <FiClock className="text-yellow-600" />;
      default:
        return <FiClock className="text-slate-600" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FiAlertTriangle className="text-yellow-600 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-yellow-900">Admin Deletion - Use with Caution</h3>
            <p className="text-sm text-yellow-800 mt-1">
              This action will permanently delete all member data. Requires ticket ID for audit trail.
              Member will be notified via email (if configured).
            </p>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-900 mb-4">Search Member</h3>
        <div className="flex gap-3">
          <div className="flex-1">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Enter email or member ID..."
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={searching || !!selectedMember}
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={searching || !!selectedMember}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <FiSearch size={16} />
            {searching ? 'Searching...' : 'Search'}
          </button>
        </div>

        {/* Search Error */}
        {searchError && (
          <div className="mt-3 text-sm text-red-600 flex items-center gap-2">
            <FiAlertTriangle size={14} />
            {searchError}
          </div>
        )}

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="mt-4 border border-slate-200 rounded-lg divide-y divide-slate-200">
            {searchResults.map((member) => (
              <div
                key={member.uuid}
                className="p-4 hover:bg-slate-50 cursor-pointer"
                onClick={() => handleSelectMember(member)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">
                      {member.first_name} {member.last_name}
                    </p>
                    <p className="text-sm text-slate-600">{member.email}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Member ID: {member.member_id} • Status: {member.lifecycle_status}
                    </p>
                  </div>
                  <button className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">
                    Select
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Selected Member & Deletion Form */}
      {selectedMember && (
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">Selected Member</h3>
            <button
              onClick={handleClearSelection}
              className="text-slate-500 hover:text-slate-700"
            >
              <FiX size={20} />
            </button>
          </div>

          {/* Member Details */}
          <div className="bg-slate-50 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-500 uppercase">Name</p>
                <p className="font-medium text-slate-900">
                  {selectedMember.first_name} {selectedMember.last_name}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Email</p>
                <p className="font-medium text-slate-900">{selectedMember.email}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Member ID</p>
                <p className="font-medium text-slate-900">{selectedMember.member_id}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Status</p>
                <p className="font-medium text-slate-900">{selectedMember.lifecycle_status}</p>
              </div>
            </div>
          </div>

          {/* Deletion Form */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Deletion Reason <span className="text-red-500">*</span>
              </label>
              <select
                value={deletionReason}
                onChange={(e) => setDeletionReason(e.target.value as DeletionReason)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {DELETION_REASONS.map((reason) => (
                  <option key={reason.value} value={reason.value}>
                    {reason.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Ticket ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={ticketId}
                onChange={(e) => setTicketId(e.target.value)}
                placeholder="e.g., JIRA-1234, SUPPORT-5678"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-slate-500 mt-1">
                Required for audit trail (Jira ticket, support case, etc.)
              </p>
            </div>

            <button
              onClick={handleInitiateDeletion}
              disabled={!ticketId.trim() || submitting}
              className="w-full px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
            >
              <FiTrash2 size={16} />
              Initiate Deletion
            </button>
          </div>
        </div>
      )}

      {/* Current Job Status */}
      {currentJob && (
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4">Deletion Job Status</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Job ID:</span>
              <code className="text-xs bg-slate-100 px-2 py-1 rounded">{currentJob.job_id}</code>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Status:</span>
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full border ${getStatusColor(currentJob.status)}`}>
                {getStatusIcon(currentJob.status)}
                <span className="text-sm font-medium capitalize">{currentJob.status.replace('_', ' ')}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Created:</span>
              <span className="text-sm text-slate-900">
                {new Date(currentJob.created_at).toLocaleString()}
              </span>
            </div>
            {currentJob.completed_at && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Completed:</span>
                <span className="text-sm text-slate-900">
                  {new Date(currentJob.completed_at).toLocaleString()}
                </span>
              </div>
            )}
            {currentJob.error_message && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{currentJob.error_message}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <FiAlertTriangle className="text-red-600" size={24} />
                </div>
                <h3 className="text-lg font-semibold text-slate-900">Confirm Deletion</h3>
              </div>

              <div className="mb-6">
                <p className="text-slate-700 mb-4">
                  You are about to permanently delete all data for:
                </p>
                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                  <p className="font-medium text-slate-900">
                    {selectedMember.first_name} {selectedMember.last_name}
                  </p>
                  <p className="text-sm text-slate-600">{selectedMember.email}</p>
                  <p className="text-xs text-slate-500">Member ID: {selectedMember.member_id}</p>
                </div>
                <div className="mt-4 space-y-2">
                  <p className="text-sm text-slate-700">
                    <strong>Reason:</strong> {DELETION_REASONS.find(r => r.value === deletionReason)?.label}
                  </p>
                  <p className="text-sm text-slate-700">
                    <strong>Ticket ID:</strong> {ticketId}
                  </p>
                </div>
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    ⚠️ This action cannot be undone. All member data will be permanently deleted after a 30-day cooling-off period.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleCancelDialog}
                  className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDeletion}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                >
                  Confirm Deletion
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDeletionPanel;

